import { Component, type ErrorInfo, type ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import { track } from '../services/heliosTelemetry';

interface Props { children: ReactNode; fallbackName?: string; }
interface State { hasError: boolean; errorMessage: string; }

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, errorMessage: '' };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, errorMessage: error.message };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error(`[ErrorBoundary: ${this.props.fallbackName ?? 'unknown'}]`, error, errorInfo);
    Sentry.captureException(error, { extra: { component: this.props.fallbackName, componentStack: errorInfo.componentStack } });
    try {
      let hash = 0;
      const s = error.stack?.slice(0, 1000) ?? '';
      for (let i = 0; i < s.length; i++) { hash = ((hash << 5) - hash) + s.charCodeAt(i); hash |= 0; }
      track('client_error', {
        error_message: error.message?.slice(0, 500),
        component: this.props.fallbackName ?? errorInfo.componentStack?.split('\n')[1]?.trim()?.slice(0, 200) ?? 'unknown',
        stack_trace_hash: Math.abs(hash).toString(36),
      });
    } catch { /* telemetry must never throw */ }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '40px 24px',
          textAlign: 'center',
        }}>
          <p style={{ fontSize: '13px', color: 'var(--color-text-secondary)', marginBottom: '8px' }}>
            {this.props.fallbackName ?? 'This section'} ran into an issue.
          </p>
          <p style={{ fontSize: '11px', color: '#4A4A4A', marginBottom: '20px', fontFamily: 'monospace' }}>
            {this.state.errorMessage}
          </p>
          <button
            onClick={() => this.setState({ hasError: false, errorMessage: '' })}
            style={{
              padding: '8px 20px',
              border: '1px solid var(--color-border)',
              background: 'transparent',
              color: 'var(--color-text-secondary)',
              borderRadius: '8px',
              fontSize: '12px',
              cursor: 'pointer',
            }}
          >
            Try again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
